import { normalizeLang } from '../i18n/index.js';
import { cleanSitePatch, splitIntakeList, websiteSourceMetadata } from './websiteIntake.js';

export const BRAND_CREATED_EVENT = 'dathent:brand-created';

export function accountBrandName(user) {
  return String(user?.name || user?.fullName || user?.email?.split('@')[0] || '').trim();
}

export function normalizeQuickBrandWebsite(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  const candidate = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  const parsed = new URL(candidate);
  if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error('INVALID_WEBSITE');
  return parsed.toString();
}

// Mirrors the MCP submit_brand_basics → confirm_brand_setup path through the
// SPA-callable gateway: start a session, save the same minimal answers, then
// let onboarding-worker create the canonical profile + adopt orphan socials.
export async function createMinimalBrand({ apiClient, businessName, website = '', language = 'en' }) {
  const name = String(businessName || '').trim();
  if (!name) throw new Error('BRAND_NAME_REQUIRED');
  const websiteUrl = normalizeQuickBrandWebsite(website);
  const contentLanguage = normalizeLang(language) || 'en';

  await apiClient.onboarding.start();
  await apiClient.onboarding.saveMessage({ stepKey: 'business_name', answerText: name });
  if (websiteUrl) {
    await apiClient.onboarding.saveMessage({ stepKey: 'website', answerText: websiteUrl });
  }
  await apiClient.onboarding.saveMessage({ stepKey: 'language', answerText: contentLanguage });
  return apiClient.onboarding.complete();
}

export function isMeaningfulBusinessDescription(value) {
  const text = String(value || '').trim().replace(/\s+/g, ' ');
  return text.length >= 5 && !['business description not provided', 'target audience not provided'].includes(text.toLowerCase());
}

function unwrap(result) {
  return result?.data && typeof result.data === 'object' ? result.data : result;
}

export async function scanQuickBrandWebsite({ apiClient, businessName, website, rasterizeLogo }) {
  const websiteUrl = normalizeQuickBrandWebsite(website);
  if (!websiteUrl) throw new Error('WEBSITE_REQUIRED');
  await apiClient.onboarding.start();
  const [websiteResult, brandResult] = await Promise.all([
    apiClient.onboarding.analyzeWebsite({ url: websiteUrl }).then(unwrap).catch(() => null),
    apiClient.onboarding.detectBrand({ url: websiteUrl, brandName: String(businessName || '').trim() || undefined }).then(unwrap).catch(() => null),
  ]);
  if (!websiteResult && !brandResult) throw new Error('WEBSITE_SCAN_FAILED');
  const fields = cleanSitePatch(websiteResult?.suggestedProfilePatch);
  let logo = brandResult?.found && brandResult.logo?.dataUrl ? { dataUrl: brandResult.logo.dataUrl } : null;
  let logoUnavailable = false;
  if (logo && String(brandResult.logo?.mimeType || '').includes('svg')) {
    try { logo = { dataUrl: await rasterizeLogo(logo.dataUrl) }; } catch { logo = null; logoUnavailable = true; }
  }
  return {
    url: websiteResult?.url || websiteUrl,
    confidence: websiteResult?.confidence,
    domain: brandResult?.domain || new URL(websiteUrl).hostname,
    // IntakeCard edits and emits text. Its array fields must be display strings.
    business: { ...fields, ...(fields.contentPillars ? { contentPillars: fields.contentPillars.join(' · ') } : {}) },
    logo,
    logoUnavailable,
    colors: brandResult?.found && Array.isArray(brandResult.colors) ? brandResult.colors : [],
    styleTags: brandResult?.found && Array.isArray(brandResult.styleTags) ? brandResult.styleTags : [],
  };
}

export async function createReviewedQuickBrand({ apiClient, businessName, website, language = 'en', reviewed, confidence, websiteAnalysis, manualDescription = false }) {
  const name = String(businessName || '').trim();
  if (!name) throw new Error('BRAND_NAME_REQUIRED');
  const websiteUrl = normalizeQuickBrandWebsite(website);
  const contentLanguage = normalizeLang(language) || 'en';
  const fields = reviewed?.fields || {};
  if (!isMeaningfulBusinessDescription(fields.businessDescription)) throw new Error('BUSINESS_DESCRIPTION_REQUIRED');

  const patch = {};
  ['businessDescription', 'offer', 'targetAudience', 'toneOfVoice', 'productOrService', 'visualStyle']
    .forEach((key) => { if (typeof fields[key] === 'string' && fields[key].trim()) patch[key] = fields[key].trim(); });
  if (fields.contentPillars) patch.contentPillars = splitIntakeList(fields.contentPillars);
  if (fields.goals) patch.goals = splitIntakeList(fields.goals);
  if (fields.painPoints) patch.painPoints = splitIntakeList(fields.painPoints);
  if (Array.isArray(reviewed?.colors) && reviewed.colors.length) patch.brandColors = reviewed.colors;
  if (websiteAnalysis && typeof websiteAnalysis === 'object' && !Array.isArray(websiteAnalysis)) patch.websiteAnalysis = websiteAnalysis;

  const sourceMetadata = websiteSourceMetadata(patch, confidence);
  if (manualDescription) sourceMetadata.businessDescription = { source: 'user_answer', confidence: 1, needsReview: false, updatedAt: new Date().toISOString() };
  // Also resumes a session when a failed scan is followed by a manual brief.
  await apiClient.onboarding.start();
  await apiClient.onboarding.saveMessage({ stepKey: 'business_name', answerText: name });
  await apiClient.onboarding.saveMessage({ stepKey: 'website', answerText: websiteUrl });
  await apiClient.onboarding.saveMessage({ stepKey: 'language', answerText: contentLanguage });
  await apiClient.onboarding.saveMessage({
    stepKey: 'website_analysis',
    question: 'Site scan reviewed & confirmed',
    answerText: `Confirmed ${websiteUrl}`,
    extracted: { ...patch, sourceMetadata },
  });
  if (reviewed?.logo?.dataUrl) {
    try {
      await apiClient.onboarding.addReference({ type: 'logo', fileName: 'brand-logo.png', dataUrl: reviewed.logo.dataUrl });
    } catch (cause) {
      const error = new Error('LOGO_SAVE_FAILED', { cause });
      error.code = 'LOGO_SAVE_FAILED';
      throw error;
    }
  }
  return apiClient.onboarding.complete();
}
